import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, deleteDoc, doc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import app, { db } from "../../firebase/client";
import { signOut } from "../../auth/firebaseAuth";
import "../../styles/ui.css";

const functions = getFunctions(app, "us-central1");
const removeCaregiverFn = httpsCallable(functions, "removeCaregiver");
const inviteFn = httpsCallable(functions, "invite");
const deleteAccountFn = httpsCallable(functions, "deleteAccount");

// Admin-only modal, opened on demand from the board: invite caregivers, list
// active caregivers (Remove = delete membership + account), and list pending
// invites (Revoke). Hidden until needed so it doesn't clutter the daily board.
export default function ManageCaregivers({ open, onClose, tenantId, myUid }) {
  const [caregivers, setCaregivers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [notice, setNotice] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // The admin can only delete the care circle once they're the last member.
  const soleMember = caregivers.length <= 1;

  // Live tenant-scoped subscriptions, only while the modal is open.
  useEffect(() => {
    if (!open || !tenantId) return;
    const uUnsub = onSnapshot(
      query(collection(db, "users"), where("tenantId", "==", tenantId)),
      (s) => setCaregivers(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setNotice({ type: "error", text: "Could not load Care Circle members." })
    );
    const iUnsub = onSnapshot(
      query(collection(db, "invites"), where("tenantId", "==", tenantId)),
      (s) => setInvites(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => { /* invites read is admin-only; ignore transient errors */ }
    );
    return () => { uUnsub(); iUnsub(); };
  }, [open, tenantId]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const invite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setNotice(null);
    try {
      const res = await inviteFn({ email, role: "caregiver" });
      setInviteEmail("");
      const sent = res && res.data && res.data.emailSent;
      setNotice({
        type: "ok",
        text: sent
          ? `Invited ${email} — we emailed them a sign-in link.`
          : `Invited ${email}. Share this sign-in link with them: ${window.location.origin}/signin`,
      });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Could not create the invite." });
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They lose access immediately.`)) return;
    setNotice(null);
    try {
      await removeCaregiverFn({ uid: u.id });
      setNotice({ type: "ok", text: `Removed ${u.email}.` });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Could not remove this Care Circle member." });
    }
  };

  const revoke = async (inv) => {
    setNotice(null);
    try {
      await deleteDoc(doc(db, "invites", inv.id));
      setNotice({ type: "ok", text: `Revoked invite for ${inv.email}.` });
    } catch (err) {
      setNotice({ type: "error", text: "Could not revoke invite." });
    }
  };

  const deleteAccount = async () => {
    if (deleteText !== "DELETE") return;
    setDeleting(true);
    setNotice(null);
    try {
      await deleteAccountFn();
      await signOut();
      window.location.assign("/"); // care circle is gone — back to the landing page
    } catch (err) {
      setDeleting(false);
      setNotice({ type: "error", text: err.message || "Could not delete the account." });
    }
  };

  return (
    <div className="mui mui-overlay" onClick={onClose}>
      <div className="mui-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mui-head">
          <h3 className="mui-title">Manage Care Circle</h3>
          <button className="mui-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <p className="mui-sub">Invite a Care Circle member</p>
        <div className="mui-inline" style={{ margin: "0.4rem 0 0.6rem" }}>
          <input
            className="mui-field"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && invite()}
            placeholder="email@example.com"
          />
          <button className="mui-btn mui-btn-primary" onClick={invite}>Invite</button>
        </div>

        <h4 className="mui-h4">Care Circle members</h4>
        {caregivers.length === 0 && <p className="mui-sub">No Care Circle members yet.</p>}
        <ul className="mui-list">
          {caregivers.map((u) => (
            <li key={u.id} className="mui-item">
              <span>{u.email} <span className="mui-tag">({u.role})</span></span>
              {u.id === myUid
                ? <span className="mui-tag">you</span>
                : <button className="mui-btn mui-btn-danger mui-btn-sm" onClick={() => remove(u)}>Remove</button>}
            </li>
          ))}
        </ul>

        {invites.length > 0 && (
          <>
            <h4 className="mui-h4">Pending invites</h4>
            <ul className="mui-list">
              {invites.map((inv) => (
                <li key={inv.id} className="mui-item">
                  <span>{inv.email} <span className="mui-tag">({inv.role})</span></span>
                  <button className="mui-btn mui-btn-danger mui-btn-sm" onClick={() => revoke(inv)}>Revoke</button>
                </li>
              ))}
            </ul>
          </>
        )}

        <div style={{ marginTop: "1.4rem", paddingTop: "1rem", borderTop: "1px solid #3a2530" }}>
          <h4 className="mui-h4" style={{ color: "var(--danger-ink)" }}>Danger zone</h4>
          {!soleMember ? (
            <p className="mui-sub">
              Remove all other Care Circle members above before you can delete the Care Circle.
            </p>
          ) : !confirmDelete ? (
            <>
              <p className="mui-sub">
                Permanently delete this Care Circle — all residents, messages, connected
                displays, your billing subscription, and your login. This cannot be undone.
              </p>
              <button className="mui-btn mui-btn-danger" onClick={() => { setDeleteText(""); setConfirmDelete(true); }}>
                Delete account
              </button>
            </>
          ) : (
            <>
              <p className="mui-sub">Type <strong>DELETE</strong> to confirm. This is permanent.</p>
              <div className="mui-inline" style={{ margin: "0.4rem 0 0.6rem" }}>
                <input
                  className="mui-field"
                  autoFocus
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && deleteAccount()}
                  placeholder="DELETE"
                  disabled={deleting}
                />
              </div>
              <div className="mui-inline">
                <button className="mui-btn mui-btn-outline" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</button>
                <button className="mui-btn mui-btn-danger" onClick={deleteAccount} disabled={deleting || deleteText !== "DELETE"}>
                  {deleting ? "Deleting…" : "Delete permanently"}
                </button>
              </div>
            </>
          )}
        </div>

        {notice && (
          <p className={notice.type === "ok" ? "mui-msg mui-ok" : "mui-msg mui-error"}>{notice.text}</p>
        )}
      </div>
    </div>
  );
}
